import request, { ContentType, BaseService, type ApiResponse } from './api'
import qs from 'qs'
import { RemoteRunnable } from '@langchain/core/runnables/remote'
import { ecp, MessageManager } from '@/utils/rangy'
import { omit } from 'xe-utils'
import { DeviationDetail, Status } from './index'

export type Model = 'q2_7b' | 'q2_72b' | 'q1half_14b' | 'q1half_32b' | 'm4_9b' // 应用的模型
export type Comment = 0 | 1 | 2 // 0，无评价；1，赞；2，踩
type Machine = {
  machine: string // 多轮对话的回答
  candidate: { zh_cn: string; en_us: string }[] // 备选回答
  model: Model
  machine_eng: string // 英文回答
  comment: Comment
  id: string
  original_content?: string // 回答元数据
}
export interface DialogRecord {
  dialog: {
    dlg_id: string //主键id
    content: {
      report_id: string //报告id
      user: string //问题
      machine: string //回答
      elements?: string[] // 要素检查
      elementIsShow: boolean // 前端添加的属性，用于记录是否显示要素检查
      candidate: { zh_cn: string; en_us: string }[] // 备选回答
      machine_eng: string // 英文回答
      order: number //顺序
      temp_report: string //生成报告
      temp_report_eng: string // 英文报告
      available: 0 | 1 //0：有效，1：隐藏
      isLoading?: boolean // 前端添加的属性，用于记录是否正在获取数据
      isGenReport?: boolean // 前端添加的属性，用于记录是否正在生成报告/摘要(stream建立连接)
      isGenning?: boolean // 前端添加的属性，用于记录是否正在生成报告/摘要(回答中)
      model?: Model // 前端添加的属性，记录当前选中的模型
      pageCount?: number // 前端添加属性, 记录多轮对话当前active的对话，回答总数
      page?: number // 前端添加属性，记录多轮对话当前active的对话，当前选中的回答page
      isEdit?: boolean // 前端添加属性，记录是否正在编辑问题
      active_id?: string
      comment?: Comment // 前端添加属性，用于记录赞和踩
      multi_rounds: Array<{
        // 用于记录多轮对话
        user: string // 多轮对话的问题
        account: string // 用户账户
        machines: Machine[]
      }>
      active_report_id: string // 当前选中的报告id
      report: string // 前端添加的属性，当前选中的报告
      report_eng: string // 前端添加的属性，当前选中的报告英文
      multi_reports: Array<{
        // 用于记录生成的多轮报告
        id: string
        report: string
        report_eng: string
      }>
    }[]
    advice: string // 返回调整的报告建议
    title: string //标题
    create_time: string //创建时间
    update_time: string //更新时间
    delete_flag: DeleteFlag
    status: DeleteFlag
  }
}

export interface EventRecord {
  event: {
    event_id: string // 事件id
    qa_dialog: DialogRecord['dialog'] // 第一轮对话记录
    report_dialog: DialogRecord['dialog'] | null // 第二轮对话记录
    ai_review: Pick<DeviationDetail, 'deviation' | 'score' | 'status'> | null // 第三轮对话记录
    qa_review: Array<{
      content: string
      review_time: string | null
      status: Status
    }> | null // 第四轮qa审核
    round: 1 | 2 | 3 | 4 // 当前进行到第几步
    delete_flag: DeleteFlag
    create_time: string
    update_time: string
  }
}

export interface AddDialog {
  dialog: Omit<DialogRecord['dialog'], 'title' | 'content'> & {
    content: DialogRecord['dialog']['content'][0]
  }
}

export interface HistoryRecord {
  page: {
    list: {
      event_id: EventRecord['event']['event_id']
      title: string
      delete_flag: DeleteFlag
      create_time: string
    }[]
    total: number
    pageSize: number
    pageNo: number
    pages: number
  }
}

export interface EventInfo {
  ai_eva_id: null
  create_time: string
  delete_flag: DeleteFlag
  eva_id: null
  event_id: EventRecord['event']['event_id']
  qa_dlg_id: string
  report_dlg_id: string
  title: string
  update_time: string
}

type RunInput = {
  messages: Array<{ content: string; type: 'system' | 'human' | 'ai' }>
}

type Output = { content: string; action: 'question' | 'finish' }

type ReportAdjustInput = {
  history_messages: Array<{
    content: string
    type: 'chat'
    role: 'user' | 'assistant'
  }> // 历史对话
  report_content: string // 最新的报告内容
  user_input: string // 最后一轮用户输入的内容
}

type ReportAdjustOutput = {
  raw: {
    content: string
  }
  data:
    | {
        action: 'question'
        question_output: {
          content: string // 反问用户
          todo_instructions: string[]
        }
        thought: string
      }
    | {
        action: 'rewrite'
        rewrite_output: {
          new_report: string // 重写报告
        }
        thought: string
      }
    | {
        action: 'edit'
        edit_output: Array<{ origin: string; update: string }> // 增量更新
        thought: string
      }
}

export type ElementCheckOutput = {
  std_id_map: Array<{
    content: string
    id: string
    idx: number
    ori_id: string
  }>
  data: {
    raw: { content: string }
    check_result: Array<{
      std_id: string
      reason: string
      level: 'A' | 'B' | 'C'
      inapplicable: 0 | 1
    }>
  }
}

export type StreamOptions = {
  onMessage?: (msg: string) => void
  onError?: (err: unknown) => void
  configurable?: { model?: Model; lang: Lang }
}

class DialogService extends BaseService {
  private _formatContent(content: DialogRecord['dialog']['content'][0]) {
    if (!content.multi_rounds) {
      let id = ecp.utils.uid()
      content.multi_rounds = [
        {
          user: content.user,
          account: '',
          machines: [
            {
              id,
              comment: 0,
              machine: content.machine,
              candidate: content.candidate || [
                { zh_cn: content.machine, en_us: content.machine_eng }
              ],
              machine_eng: content.machine_eng,
              model: content.model || 'q2_72b'
            }
          ]
        }
      ]
      content.active_id = id
    }
    if (!content.multi_reports) {
      content.multi_reports = []
    }
    if (content.active_report_id) {
      let report = content.multi_reports.find(
        (item) => item.id === content.active_report_id
      )!
      content.report = report.report
      content.report_eng = report.report_eng
    }
    let { machine, page, pageCount } = this.findMachineById(
      content.active_id!,
      content
    )
    if (machine) {
      content.comment = machine.comment
      content.model = machine.model
      content.machine = machine.machine
      content.machine_eng = machine.machine_eng
      content.page = page
      content.pageCount = pageCount
      content.elementIsShow = true
    }
  }

  // 1.1 查：查看对话记录
  async getDialogRecord(dlg_id: DialogRecord['dialog']['dlg_id']) {
    let res = await request.get<ApiResponse<DialogRecord>>(
      `${this.prefix}/dialog/${dlg_id}`
    )
    if (res?.data && this.isSuccess(res.data)) {
      res.data.data.dialog.content.forEach(this._formatContent.bind(this))
    }

    return res!.data
  }

  /**
   * @description 1.2 查：查看事件记录
   */
  async getEventRecord(event_id: EventRecord['event']['event_id']) {
    let res = await request.get<ApiResponse<EventRecord>>(
      `${this.prefix}/dialog/${event_id}/get`
    )
    if (res?.data && this.isSuccess(res.data)) {
      res.data.data.event.qa_dialog['content'].forEach(
        this._formatContent.bind(this)
      )
      if (res.data.data.event.report_dialog) {
        res.data.data.event.report_dialog['content'].forEach(
          this._formatContent.bind(this)
        )
      }
    }
    return res!.data
  }

  /**
   * @description 1.2 增：新增对话
   * @param status: 0：正常回答，1：重新回答
   */
  async addDialog(params: {
    dlg_id?: DialogRecord['dialog']['dlg_id']
    user: string
    status: 0 | 1
  }) {
    let res = await request.post<ApiResponse<AddDialog>>(
      `${this.prefix}/dialog/add`,
      params,
      {
        headers: {
          'Content-Type': ContentType.json
        }
      }
    )
    return res!.data
  }

  /**
   * @description 1.3 增：保存对话内容（临时）
   */
  async addDialogTemp(params: {
    dlg_id: DialogRecord['dialog']['dlg_id']
    content: DialogRecord['dialog']['content']
  }) {
    params.content = params.content.map((content) =>
      omit(content, [
        'isLoading',
        'isGenReport',
        'isGenning',
        'model',
        'pageCount',
        'comment',
        'page',
        'isEdit',
        'report',
        'report_eng',
        'elementIsShow'
      ])
    )
    let res = await request.postJson<ApiResponse<{ eventInfo: EventInfo }>>(
      `${this.prefix}/dialog/save`,
      params
    )
    return res!.data
  }

  /**
   * @description 1.5 增：保存报告调整
   */
  async addReportAdjustment(
    event_id: EventRecord['event']['event_id'],
    params: {
      dlg_id: DialogRecord['dialog']['dlg_id']
      content: DialogRecord['dialog']['content']
    }
  ) {
    params.content = params.content.map((content) =>
      omit(content, [
        'isLoading',
        'isGenReport',
        'isGenning',
        'model',
        'pageCount',
        'comment',
        'page',
        'isEdit',
        'report',
        'report_eng',
        'elementIsShow'
      ])
    )
    let res = await request.postJson(
      `${this.prefix}/dialog/${event_id}/save`,
      params
    )
    return res!.data
  }

  /**
   * @description 1.7 查：查看历史事件
   */
  async getHistoryRecord(params: { page_number: number; page_size: number }) {
    let res = await request.get<ApiResponse<HistoryRecord>>(
      `${this.prefix}/dialog/history`,
      {
        query: params
      }
    )
    return res!.data
  }

  /**
   * @description 1.8 删：修改/删除历史事件
   * @param delete_flag 0-正常 1-删除
   */
  async delHistoryRecord(
    dlg_id: DialogRecord['dialog']['dlg_id'],
    params: {
      delete_flag: DeleteFlag
      title: string
    }
  ) {
    let res = await request.post(
      `${this.prefix}/dialog/${dlg_id}/update?${qs.stringify(params)}`
    )
    return res!.data
  }

  /**
   * @description 将传入的对话内容转化为 RemoteRunnable 需要的参数 messages
   * @param reverse type参数: human 和 ai 是否需要翻转，在生成问题接口(调试用): /devi_reporter 为 true
   */
  private _transContentToMessages(
    contents: DialogRecord['dialog']['content'],
    { reverse = false } = {}
  ) {
    return contents.reduce<RunInput['messages']>((res, content) => {
      let { user, machine } = content
      res.push({
        content: user,
        type: reverse ? 'ai' : 'human'
      })
      if (machine) {
        res.push({
          content: machine,
          type: reverse ? 'human' : 'ai'
        })
      }
      return res
    }, [])
  }

  /**
   * @description 获取模型回答
   */
  async getAnswer(
    contents: DialogRecord['dialog']['content'],
    configurable: StreamOptions['configurable'] = {
      lang: 'zh-cn'
    }
  ) {
    const remoteChain = new RemoteRunnable<
      RunInput,
      Output & { candidate: string[] },
      {}
    >({
      url: `${this.streamPrefix}/devi_investigator`
    })
    let messages = this._transContentToMessages(contents)
    return remoteChain
      .invoke(
        {
          messages
        },
        {
          configurable: {
            ...configurable
          }
        }
      )
      .catch((e: Error): Output & { candidate: string[] } => {
        ecp.utils.message.error(e.message)
        return { content: '', action: 'question', candidate: [] }
      })
    // let messages = contents.reduce<RunInput['messages']>(
    //   (res, content) => {
    //     let { user, machine } = content
    //     res.push({
    //       content: user,
    //       type: 'human'
    //     })
    //     if (machine) {
    //       res.push({
    //         content: machine,
    //         type: 'ai'
    //       })
    //     }
    //     return res
    //   },
    //   [
    //     {
    //       content:
    //         '你扮演药厂偏差事件的助理角色；对话开始时，用户输入偏差的基本情况。你按一问一答地方式了解偏差的详细信息，获得偏差事件的完整信息要素。最后信息都搜集完毕后，请结束偏差对话，总结成多段文字描述完整偏差事件。请每次只提问一个问题。\n完整的偏差完整信息要素包含: 谁发现/如何发现、什么事情、在哪里发现、发现日期、发生日期(如果知道)、未满足要求和被认为受到影响的对象(即材料、产品、组件、设备和人员)，包括对象的唯一标识符(批号、设备ID、员工ID号等)。',
    //       type: 'system'
    //     }
    //   ]
    // )
    // return remoteChain.invoke({
    //   messages
    // })
  }

  /**
   * @description ai生成人工输入问题
   * @todo 增加错误处理的函数(状态码不为200)
   */
  async getDebugQuestion(
    contents: DialogRecord['dialog']['content'],
    desc: string
  ) {
    const lang = (localStorage.getItem('lang') ||
      'zh-cn') as NotNullOrUndefined<StreamOptions['configurable']>['lang']
    const remoteChain = new RemoteRunnable<
      RunInput & { deviation_description: string },
      string,
      {}
    >({
      url: `${this.streamPrefix}/devi_reporter`
    })
    let messages = this._transContentToMessages(contents, { reverse: true })
    return remoteChain.invoke(
      {
        messages,
        deviation_description: desc
      },
      {
        configurable: {
          lang
        }
      }
    )
  }

  /**
   * @description 根据偏差对话内容生成报告
   */
  async getReport(
    contents: DialogRecord['dialog']['content'],
    options?: StreamOptions
  ) {
    const { resolve, promise } = ecp.utils.withResolvers<string>()
    const remoteChain = new RemoteRunnable<
      RunInput,
      { raw: { content: string } } | { data: { report: string } },
      {}
    >({
      url: `${this.streamPrefix}/deviation_writing`,
      options: {
        timeout: 600000
      }
    })
    let messages = this._transContentToMessages(contents)
    let finalReport = ''
    const msgManager = new MessageManager({
      onMessage: options?.onMessage,
      onFinish: () => resolve(finalReport)
    })
    const stream = await remoteChain.stream(
      {
        messages
      },
      options?.configurable && {
        configurable: {
          ...options.configurable,
          writing_output_format: 'obj'
        }
      }
    )
    msgManager.start()
    try {
      for await (const chunk of stream) {
        console.log(chunk)
        if ('raw' in chunk) {
          msgManager.add(chunk.raw.content)
        } else {
          finalReport = chunk.data.report
        }
      }
    } catch (e) {
      ecp.utils.message.error('stream 连接已断开')
      console.log(e)
      options?.onError?.(e)
    } finally {
      msgManager.stop()
      return promise
    }
  }

  /**
   * @description 根据偏差对话内容生成摘要
   */
  async getSummary(
    contents: DialogRecord['dialog']['content'],
    options?: StreamOptions
  ) {
    const { resolve, promise } = ecp.utils.withResolvers()
    const remoteChain = new RemoteRunnable<RunInput, { content: string }, {}>({
      url: `${this.streamPrefix}/chat`,
      options: {
        timeout: 600000
      }
    })
    let text = contents.reduce((res, content) => res + content.user, '')
    let messages: RunInput['messages'] = [
      {
        type: 'human',
        content: `${
          options?.configurable?.lang === 'en-us'
            ? '使用英文生成内容，不管用户输入内容的语言。把格式要求的内容也使用英文。'
            : ''
        }将以下内容生成摘要（需要按照以下形式：\n发生时间: XXX\n发现时间: XXX\n受影响的对象: XXX\n以此类推\n）：${text}`
      }
    ]
    const msgManager = new MessageManager({
      onMessage: options?.onMessage,
      onFinish: resolve
    })
    const stream = await remoteChain.stream(
      {
        messages
      },
      {
        configurable: {
          llm_temperature: 0.8
        }
      }
    )
    msgManager.start()
    try {
      for await (const chunk of stream) {
        console.log(chunk)
        if (chunk.content) {
          msgManager.add(chunk.content)
        }
      }
    } catch (e) {
      ecp.utils.message.error('stream 连接已断开')
      console.log(e)
      options?.onError?.(e)
    } finally {
      msgManager.stop()
      return promise
    }
  }

  /**
   * @description 将传入的文本翻译为英文
   * @param stream 是否需要按照 stream 流式返回
   */
  async getEnglishTranslate(
    text: string,
    options?: StreamOptions & { stream?: boolean }
  ) {
    const { resolve, promise } = ecp.utils.withResolvers()
    const remoteChain = new RemoteRunnable<RunInput, { content: string }, {}>({
      url: `${this.streamPrefix}/translate`,
      options: {
        timeout: 600000
      }
    })
    let messages: RunInput['messages'] = [
      {
        content: `将下面一段话翻译为${
          options?.configurable?.lang === 'en-us' ? '中文' : '英文'
        }: ${text}`,
        type: 'human'
      }
    ]
    if (!options?.stream)
      return remoteChain.invoke({
        messages
      })
    const msgManager = new MessageManager({
      onMessage: options?.onMessage,
      onFinish: resolve
    })
    const stream = await remoteChain.stream({
      messages
    })
    msgManager.start()
    try {
      for await (const chunk of stream) {
        if (chunk.content) {
          msgManager.add(chunk.content)
        }
      }
    } catch (e) {
      ecp.utils.message.error('stream 连接已断开')
      console.log(e)
      options?.onError?.(e)
    } finally {
      msgManager.stop()
      return promise
    }
  }

  /**
   * @description 1.4 改：赞/踩
   */
  async updateComment(params: {
    id: Exclude<DialogRecord['dialog']['content'][0]['active_id'], undefined>
    comment: Comment
    report_id: string
    machine: string
    machine_eng: string
  }) {
    let res = await request.postJson(`${this.prefix}/dialog/comment`, params)
    return res!.data
  }

  /**
   * @description 根据传入的id 查找到对应的mutiRound
   */
  findMachineById(
    id: Exclude<DialogRecord['dialog']['content'][0]['active_id'], undefined>,
    content: DialogRecord['dialog']['content'][0]
  ) {
    if (!content.multi_rounds) return {}
    for (let i = 0; i < content.multi_rounds.length; i++) {
      let round = content.multi_rounds[i]
      for (let j = 0; j < round.machines.length; j++) {
        if (round.machines[j].id === id)
          return {
            page: j + 1,
            pageCount: round.machines.length,
            machine: round.machines[j]
          }
      }
    }
    return {}
  }

  /**
   * @description 传入muti_rounds中的某个 machine, 会自动将他设置为active状态，并更新content
   */
  updateContentByMachine(
    content: DialogRecord['dialog']['content'][0],
    machine: Machine,
    user: DialogRecord['dialog']['content'][0]['user']
  ) {
    const { page, pageCount } = this.findMachineById(machine.id, content)
    if (page) {
      content.user = user
      content.machine = machine.machine
      content.candidate = machine.candidate
      content.machine_eng = machine.machine_eng
      content.model = machine.model
      content.pageCount = pageCount
      content.active_id = machine.id
      content.comment = machine.comment
      content.page = page
    }
    return content
  }

  /**
   * @description 要素检查功能
   */
  async getElementCheck(
    contents: DialogRecord['dialog']['content'],
    options?: StreamOptions
  ) {
    const remoteChain = new RemoteRunnable<RunInput, ElementCheckOutput, {}>({
      url: `${this.streamPrefix}/dialog_iqs_check`,
      options: {
        timeout: 600000
      }
    })
    let messages = this._transContentToMessages(contents)
    return remoteChain.invoke(
      { messages },
      options?.configurable && {
        configurable: {
          ...options.configurable
        }
      }
    )
  }

  private _transContentToHistoryMessages(
    contents: DialogRecord['dialog']['content']
  ) {
    return contents.reduce<ReportAdjustInput['history_messages']>(
      (res, content) => {
        if (content.active_id) {
          let { machine } = this.findMachineById(content.active_id, content)
          if (machine) {
            let { user } = content
            res.push({
              content: user,
              type: 'chat',
              role: 'user'
            })
            res.push({
              content: machine.original_content || '',
              type: 'chat',
              role: 'assistant'
            })
          }
        }
        return res
      },
      []
    )
  }

  /**
   * @description 报告调整
   */
  async getReportAdjust(
    contents: DialogRecord['dialog']['content'],
    report_content: string,
    user_input: string,
    configurable: StreamOptions['configurable'] = {
      lang: 'zh-cn'
    }
  ) {
    const remoteChain = new RemoteRunnable<
      ReportAdjustInput,
      ReportAdjustOutput,
      {}
    >({
      url: `${this.streamPrefix}/report_adjust`,
      options: {
        timeout: 600000
      }
    })
    let history_messages = this._transContentToHistoryMessages(contents)
    return remoteChain.invoke(
      { history_messages, report_content, user_input },
      {
        configurable
      }
    )
  }
}

export default new DialogService()
